/*
 * src/constants/positionNames.ts
 *
 * This file defines constants and utility functions related to player position names.
 * It includes a mapping of standard position keys (e.g., 'leftForward')
 * to their display names in supported languages (currently English and Dutch).
 * This allows for consistent and internationalized display of player positions
 * throughout the application.
 */
import type { Locale } from '../i18n';
import { canonicalPositionKey } from '../utils/positionKeys';
import { getDefaultHockeyPositionLabel } from '@waedo/frontend-core/hockey-positions';

/**
 * A mapping of standard position keys to their localized display names.
 * Each position key maps to an object where language codes (Locale) are keys
 * and the translated position names are values.
 *
 * Example:
 * ```
 * {
 *   leftForward: { nl: 'Links Voor', en: 'Left Forward' },
 *   // ... other positions
 * }
 * ```
 */
export const defaultPositionNames: Record<string, Record<Locale, string>> = Object.fromEntries(
  ['leftForward', 'striker', 'rightForward', 'leftMidfield', 'centerMidfield', 'rightMidfield',
    'leftBack', 'sweeper', 'rightBack', 'lastLine', 'goalkeeper'].map(key => [key, {
      nl: getDefaultHockeyPositionLabel(key, 'nl')!, en: getDefaultHockeyPositionLabel(key, 'en')!,
    }]),
);

export const defaultPositionLayoutWithLines = {
  attack: {
    leftForward: {
      x: 25,
      y: 25,
      order: 1,
      label_translations: defaultPositionNames.leftForward,
    },
    striker: {
      x: 50,
      y: 25,
      order: 2,
      label_translations: defaultPositionNames.striker,
    },
    rightForward: {
      x: 75,
      y: 25,
      order: 3,
      label_translations: defaultPositionNames.rightForward,
    },
    line_label: 'attack',
    line_order: 1,
    line_y: 25,
    line_label_translations: {
      nl: 'Aanval',
      en: 'Attack',
    },
  },
  midfield: {
    leftMidfield: {
      x: 20,
      y: 45,
      order: 4,
      label_translations: defaultPositionNames.leftMidfield,
    },
    centerMidfield: {
      x: 50,
      y: 45,
      order: 5,
      label_translations: defaultPositionNames.centerMidfield,
    },
    rightMidfield: {
      x: 80,
      y: 45,
      order: 6,
      label_translations: defaultPositionNames.rightMidfield,
    },
    line_label: 'midfield',
    line_order: 2,
    line_y: 45,
    line_label_translations: {
      nl: 'Middenveld',
      en: 'Midfield',
    },
  },
  defense: {
    leftBack: {
      x: 20,
      y: 65,
      order: 7,
      label_translations: defaultPositionNames.leftBack,
    },
    sweeper: {
      x: 50,
      y: 65,
      order: 8,
      label_translations: defaultPositionNames.sweeper,
    },
    rightBack: {
      x: 80,
      y: 65,
      order: 9,
      label_translations: defaultPositionNames.rightBack,
    },
    lastLine: {
      x: 50,
      y: 75,
      order: 10,
      label_translations: defaultPositionNames.lastLine,
    },
    line_label: 'defense',
    line_order: 3,
    line_y: 65,
    line_label_translations: {
      nl: 'Verdediging',
      en: 'Defense',
    },
  },
  goal: {
    goalkeeper: {
      x: 50,
      y: 90,
      order: 11,
      label_translations: defaultPositionNames.goalkeeper,
    },
    line_label: 'goal',
    line_order: 4,
    line_y: 90,
    line_label_translations: {
      nl: 'Keeper',
      en: 'Goal',
    },
  },
} as const;

/**
 * Retrieves the localized display label for a given position key and locale.
 * If a translation for the specified locale is not found, it falls back to Dutch ('nl').
 * If no translation is found at all for the position key, it returns the position key itself.
 *
 * @param position - The standard key for the position (e.g., 'leftForward').
 * @param locale - The desired locale for the display name (e.g., 'en', 'nl').
 * @returns The localized display name for the position, or the position key if no translation is available.
 */
export const getDefaultPositionLabel = (position: string, locale: Locale): string => {
  return (
    getDefaultHockeyPositionLabel(position, locale) ||
    defaultPositionNames[canonicalPositionKey(position)]?.[locale] ||
    defaultPositionNames[canonicalPositionKey(position)]?.nl ||
    position
  );
};
